import { useState, useEffect, useRef } from 'react';
import { Volume2, Pause, Play, Square, Download } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Textarea } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Slider } from '@/components/ui/Slider';
import { Badge } from '@/components/ui/Badge';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';

export function TextToSpeech() {
  const [text, setText] = useState(
    'Welcome to Utility OS. Paste any text here and the browser will speak it aloud.',
  );
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [voice, setVoice] = useState('');
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [vol, setVol] = useState(1);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    function load() {
      const v = window.speechSynthesis.getVoices();
      setVoices(v);
      if (v.length && !voice) {
        const def = v.find((x) => x.default) ?? v[0];
        setVoice(def.voiceURI);
      }
    }
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => {
      window.speechSynthesis.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function speak() {
    if (!text.trim()) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const sel = voices.find((v) => v.voiceURI === voice);
    if (sel) u.voice = sel;
    u.rate = rate;
    u.pitch = pitch;
    u.volume = vol;
    u.onstart = () => setSpeaking(true);
    u.onend = () => {
      setSpeaking(false);
      setPaused(false);
    };
    u.onerror = () => {
      setSpeaking(false);
      setPaused(false);
    };
    window.speechSynthesis.speak(u);
  }

  function pause() {
    window.speechSynthesis.pause();
    setPaused(true);
  }

  function resume() {
    window.speechSynthesis.resume();
    setPaused(false);
  }

  function stop() {
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setPaused(false);
  }

  async function downloadWav() {
    if (!text.trim()) return;
    try {
      const stream = (
        await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: { suppressLocalAudioPlayback: false } as MediaTrackConstraints,
        })
      ).getAudioTracks();
      if (stream.length === 0) {
        toast.error('Share a tab with audio enabled to capture');
        return;
      }
      const ms = new MediaStream(stream);
      const rec = new MediaRecorder(ms);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        saveAs(blob, 'speech.webm');
        ms.getTracks().forEach((t) => t.stop());
        setRecording(false);
      };
      rec.start();
      setRecording(true);
      speak();
      const u = new SpeechSynthesisUtterance(text);
      u.onend = () => rec.stop();
      // duplicate end-handler since speak() already started — guard:
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch {
      toast.error('Recording cancelled');
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="text-to-speech"
        title="Text to speech"
        description="Read text aloud with the browser's built-in voices. Local, free, and offline."
        badges={
          speaking ? (
            <Badge variant="accent">
              <span className="size-1.5 rounded-full bg-accent animate-pulse mr-1" />
              {paused ? 'paused' : 'speaking'}
            </Badge>
          ) : null
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-3">
          <Field label="Voice">
            <Select
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
              options={voices.map((v) => ({
                value: v.voiceURI,
                label: `${v.name} · ${v.lang}`,
              }))}
            />
          </Field>
          <Field label="Rate" hint={`${rate.toFixed(1)}×`}>
            <Slider value={rate} onChange={setRate} min={0.5} max={2} step={0.1} showValue={false} />
          </Field>
          <Field label="Pitch" hint={pitch.toFixed(1)}>
            <Slider value={pitch} onChange={setPitch} min={0} max={2} step={0.1} showValue={false} />
          </Field>
          <Field label="Volume" hint={`${Math.round(vol * 100)}%`}>
            <Slider value={vol} onChange={setVol} min={0} max={1} step={0.05} showValue={false} />
          </Field>
          <div className="grid grid-cols-2 gap-2 border-t border-border pt-3">
            {!speaking ? (
              <Button variant="primary" size="sm" onClick={speak}>
                <Play size={11} />
                Speak
              </Button>
            ) : paused ? (
              <Button variant="primary" size="sm" onClick={resume}>
                <Play size={11} />
                Resume
              </Button>
            ) : (
              <Button variant="secondary" size="sm" onClick={pause}>
                <Pause size={11} />
                Pause
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={stop} disabled={!speaking}>
              <Square size={11} />
              Stop
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={downloadWav}
            disabled={!text || speaking || recording}
          >
            <Download size={11} />
            Record to file
          </Button>
          <div className="text-2xs text-fg-subtle leading-relaxed border-t border-border pt-2">
            Recording uses screen-share with audio (Chrome/Edge) — share this tab with audio.
          </div>
        </Card>

        <Card className="lg:col-span-2 lg:order-1 !p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center gap-2 text-xs font-medium text-fg-muted">
            <Volume2 size={12} />
            Text
          </div>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="!border-0 !rounded-t-none min-h-[480px] !bg-transparent focus-visible:!ring-0"
          />
        </Card>
      </div>
    </div>
  );
}

export default TextToSpeech;
